import React from "react";
import { useEffect } from "react";
import { getLeads } from "../../queries/conv";
import { getMentors } from "../../queries/mentor";
import OpenAI from "openai";
import { Avatar, Chip, CircularProgress, Tooltip } from "@mui/material";
import moment from "moment/moment";
import { IoLogoWhatsapp } from "react-icons/io5";
import Analysis from "./Analysis";
import { GActions, useGlobalContext } from "../../contexts/GlobalContext";
import { BASE_URL } from "../../queries/url";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Done } from "@material-ui/icons";
import axios from "axios";
import { KEYS } from "../../config/env";

const Index = () => {
    const [state, globalDispatch] = useGlobalContext();
    const [downloading, setDownloading] = React.useState("nope");

    // TODO: move this call behind the backend; the key ships in the browser bundle
    const openai = new OpenAI({
        apiKey: KEYS.OPENAI,
        dangerouslyAllowBrowser: true,
    });

    useEffect(() => {
        if (!state.mentors) {
            globalDispatch({
                type: GActions.SET_MENTORS_LOADING,
                payload: true,
            });
            getMentors()
                .then((res) => {
                    globalDispatch({
                        type: GActions.SET_MENTORS,
                        payload: res,
                    });
                })
                .finally(() => {
                    globalDispatch({
                        type: GActions.SET_MENTORS_LOADING,
                        payload: false,
                    });
                });
        }

        if (!state.conversations) {
            globalDispatch({
                type: GActions.SET_CONVERSATION_LOADING,
                payload: true,
            });
            getLeads().then((res) => {
                // Apply program filtering
                let filteredLeads = res.leads;
                if (state.userProgramAccess?.role === "program_user" &&
                    state.userProgramAccess?.assignedPrograms?.length > 0) {
                    filteredLeads = res.leads.filter(lead =>
                        state.userProgramAccess.assignedPrograms.includes(lead.program)
                    );
                    if(state.user?.privilege === "usc"){
                        filteredLeads = res.leads
                    }
                }
                globalDispatch({
                    type: GActions.SET_CONVERSATIONS,
                    payload: filteredLeads,
                });
            });
            globalDispatch({
                type: GActions.SET_CONVERSATION_LOADING,
                payload: false,
            });
        }
    }, []);

    const onConvClick = (conv) => {
        globalDispatch({
            type: GActions.SET_SUMMARY,
            payload: null,
        });

        globalDispatch({
            type: GActions.SET_SELECTED_CONVERSATION,
            payload: conv,
        });
        const mentor = state.mentors?.find((m) => m._id === conv.mentorId);
        globalDispatch({
            type: GActions.SET_SELECTED_MENTOR,
            payload: mentor,
        });
    };

    const generateResponse = async (text) => {
        if (!KEYS.OPENAI) {
            return {
                error: {
                    message:
                        "OpenAI API key not configured, please follow instructions in README.md",
                },
            };
        }
        try {
            const words = 200;
            const response = await openai.chat.completions.create({
                model: "gpt-4o",
                messages: [
                    {
                        role: "system",
                        content: `You are an AI language model, tasked to generate concise and accurate 
      summaries of conversations between prospective applicants and student ambassadors
       from different universities. The conversation data you will be given is an array
        of objects, where each object contains a 'sender' attribute indicating the origin
        of the message (either 'Applicant' or 'Ambassador'), and a 'message' attribute
         containing the text of the message. Your summaries must strictly adhere to
          the details provided in the given conversation without adding, speculating,
           or inferring any additional information.`,
                    },
                    {
                        role: "user",
                        content: `Generate a summary of the following conversation: '${text}'. The length of the summary should not exceed ${words} words.`,
                    },
                ],
            });
            const result = response.choices[0].message.content;
            return result;
        } catch (error) {
            if (error instanceof OpenAI.APIError) {
                console.error(error.status, error.error);
                return { error: error.error ?? { message: error.message } };
            } else {
                console.error(
                    `Error with OpenAI API request: ${error.message}`
                );
                return {
                    error: {
                        message: "An error occurred during your request.",
                    },
                };
            }
        }
    };

    const getSummary = async () => {
        let resultAI;
        let result;
        result = JSON.stringify(state.selectedConversation?.messages);
        if (!result) {
            resultAI = "No messages were exchanged in this conversation";
        } else {
            resultAI = await generateResponse(result);
        }
        return resultAI;
    };

    const onGenerateAiSummaryButtonClick = async () => {
        globalDispatch({ type: GActions.SET_SUMMARY_LOADING, payload: true });
        try {
            const summary = await getSummary();

            globalDispatch({
                type: GActions.SET_SUMMARY,
                payload: summary,
            });
            globalDispatch({
                type: GActions.SET_SUMMARY_LOADING,
                payload: false,
            });
        } catch (error) {
            console.error("Error generating summary:", error);
            globalDispatch({
                type: GActions.SET_SUMMARY_LOADING,
                payload: false,
            });
        }
    };

    const handleExportLeads = async () => {
        try {
            setDownloading("yep");

            const loginAxios = axios.create({
                withCredentials: true,
                baseURL: BASE_URL,
            });

            const result = await loginAxios.get("/leads/summary");
            let allLeads = [];

            // Check if data is already an array of objects (not CSV string)
            if (Array.isArray(result.data)) {
                allLeads = result.data;
            } else if (typeof result.data === 'string') {
                // Parse CSV data
                const csvLines = result.data.trim().split('\n');
                allLeads = csvLines.slice(1).map(line => {
                    const fields = [];
                    let current = '';
                    let inQuotes = false;

                    for (let i = 0; i < line.length; i++) {
                        const char = line[i];
                        if (char === '"') {
                            inQuotes = !inQuotes;
                        } else if (char === ',' && !inQuotes) {
                            fields.push(current);
                            current = '';
                        } else {
                            current += char;
                        }
                    }
                    fields.push(current);

                    return {
                        firstName: fields[0]?.replace(/"/g, '') || '',
                        lastName: fields[1]?.replace(/"/g, '') || '',
                        email: fields[2]?.replace(/"/g, '') || '',
                        phone: fields[3]?.replace(/"/g, '') || '',
                        country: fields[4]?.replace(/"/g, '') || '',
                        program: fields[5]?.replace(/"/g, '') || '',
                        year: fields[6]?.replace(/"/g, '') || '',
                        type: fields[7]?.replace(/"/g, '') || '',
                        createdAt: fields[8]?.replace(/"/g, '') || ''
                    };
                }).filter(lead => lead.email);
            }

            if (allLeads.length === 0) {
                alert("No data available to export.");
                setDownloading("nope");
                return;
            }

            // Apply program filtering
            let leadsToExport = allLeads;
            if (state.userProgramAccess?.role === "program_user" &&
                state.userProgramAccess?.assignedPrograms?.length > 0) {
                leadsToExport = allLeads.filter(lead =>
                    state.userProgramAccess.assignedPrograms.includes(lead.program)
                );
                console.log("INSIGHTS EXPORT - Filtered", leadsToExport.length, "leads out of", allLeads.length);

                if (leadsToExport.length === 0) {
                    alert("No leads found for your assigned programs.");
                    setDownloading("nope");
                    return;
                }
            }

            // Convert to CSV
            if (leadsToExport.length > 0) {
                const headers = Object.keys(leadsToExport[0]).join(',');
                const rows = leadsToExport.map(lead =>
                    Object.values(lead).map(val =>
                        typeof val === 'string' && val.includes(',') ? `"${val}"` : val
                    ).join(',')
                );
                const csv = [headers, ...rows].join('\n');

                // Trigger download
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'insights_leads_export.csv';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }

            setTimeout(() => {
                setDownloading("done");
                setTimeout(() => setDownloading("nope"), 2000);
            }, 500);
        } catch (error) {
            console.error("Error exporting leads:", error);
            setDownloading("nope");
        }
    };

    return (
        <div className="grid grid-cols-6 h-screen bg-gray-50">
            <div className="col-span-2 overflow-auto max-h-screen">
                <div
                    className="flex items-center justify-between mt-12 ml-4"
                    style={{
                        fontFamily: "Nunito Sans, sans-serif",
                        fontSize: "2rem",
                        lineHeight: "1.5rem",
                        fontStyle: "normal",
                        fontWeight: 700,
                        color: "#18181B",
                    }}
                >
                    Conversations
                    <div
                        className="mr-4 cursor-pointer"
                        onClick={handleExportLeads}
                    >
                        <button className="bg-green-600 hover:bg-green-700 active:scale-95 text-white text-base font-[Inter] font-semibold px-3 py-1 rounded-md flex items-center justify-center gap-2">
                            Export{" "}
                            {downloading === "yep" && (
                                <CircularProgress size={18} color="inherit" />
                            )}
                            {downloading === "nope" && (
                                <FileDownloadIcon
                                    sx={{
                                        fontSize: "17px",
                                    }}
                                />
                            )}
                            {downloading === "done" && (
                                <Done
                                    sx={{
                                        fontSize: "17px",
                                    }}
                                />
                            )}
                        </button>
                    </div>
                </div>
                <div className="flex flex-col gap-2 mt-8">
                    {state.conversationLoading
                        ? "Loading..."
                        : state.conversations?.map((conv, i) => (
                              <ConvCard
                                  conv={conv}
                                  selected={
                                      conv._id ===
                                      state.selectedConversation?._id
                                  }
                                  prevSelected={
                                      state.conversations[i - 1]?._id ===
                                      state.selectedConversation?._id
                                  }
                                  key={conv._id}
                                  onClick={() => onConvClick(conv)}
                              />
                          ))}
                </div>
            </div>
            <div className="col-span-4 px-6 py-4 h-full overflow-auto">
                <Analysis
                    onGenerateAiSummaryButtonClick={
                        onGenerateAiSummaryButtonClick
                    }
                />
            </div>
        </div>
    );
};

const ConvCard = ({
    conv,
    selected = false,
    onClick,
    prevSelected = false,
}) => {
    const responseStudent = conv?.lastResponseStudent;
    const responseMentor = conv?.lastResponseMentor;
    const lastResponseDateStudent = new Date(responseStudent);
    const lastResponseDateMentor = new Date(responseMentor);
    let finalTime;
    if (responseStudent === null && responseMentor === null) {
        finalTime = conv.createdAt;
    } else if (
        lastResponseDateMentor !== undefined &&
        lastResponseDateStudent !== undefined
    ) {
        if (lastResponseDateMentor > lastResponseDateStudent)
            finalTime = lastResponseDateMentor;
        else finalTime = lastResponseDateStudent;
    } else if (
        lastResponseDateMentor === undefined &&
        lastResponseDateStudent !== undefined
    ) {
        finalTime = lastResponseDateStudent;
    } else if (
        lastResponseDateStudent === undefined &&
        lastResponseDateMentor !== undefined
    ) {
        finalTime = lastResponseDateMentor;
    } else {
        finalTime = conv.createdAt;
    }

    const [tipText, setTipText] = React.useState("Copy");
    const [name, setName] = React.useState("");
    const [state] = useGlobalContext();
    useEffect(() => {
        const getMentorFromId = async (mentorId) => {
            try {
                const mentor = state.mentors.find((m) => m._id === mentorId);
                if (mentor) {
                    const mentorName = mentor.name;
                    setName(mentorName);
                }
            } catch (error) {
                console.error("Error retrieving mentor name:", error);
                return null;
            }
        };

        getMentorFromId(conv.mentorId);
    }, []);

    const avatarStyle = selected
        ? {
              backgroundColor: "#7C3AED",
              height: "50px",
              width: "50px",
          }
        : {
              height: "40px",
              width: "40px",
          };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setTipText("Copied!");
        setTimeout(() => {
            setTipText("Copy");
        }, 1000);
    };

    return (
        <div
            className={`pl-3 mr-1.5 ml-3 ${
                selected ? "py-5" : "py-3"
            } pr-1 cursor-pointer  relative hover:${
                selected ? "bg-white" : "bg-purple-50"
            } hover:shadow-[rgb(119_119_119)_0px_0px_4px] hover:rounded-md hover:bg-gray-200 transition-all ${
                selected
                    ? "bg-white border-r-8 my-2 border-purple-600 shadow-[rgba(0,_0,_0,_0.44)_0px_0px_4px] rounded-md"
                    : "border-b-[1px] border-gray-200 "
            } ${prevSelected ? "border-t-[1px] border-gray-200" : ""}}`}
            onClick={onClick}
        >
            <div className="flex gap-3">
                <Avatar style={avatarStyle}>
                    {conv?.name && typeof conv?.name === "string"
                        ? conv?.name[0]
                        : ""}
                </Avatar>
                <div className="flex flex-col">
                    <span className="font-[Outfit] font-medium text-lg leading-4">
                        {conv?.name}
                    </span>
                    <span className="ml-1 font-[Outfit] text-sm mt-1 text-gray-500">
                        with {name}
                    </span>
                    {selected && (
                        <div className="flex gap-1 mt-2">
                            <Tooltip title={tipText}>
                                <Chip
                                    icon={
                                        <IoLogoWhatsapp
                                            style={{
                                                color: "white",
                                                fontSize: "14px",
                                                marginLeft: "8px",
                                            }}
                                        />
                                    }
                                    label={conv.phone}
                                    variant="filled"
                                    style={{
                                        backgroundColor: "#128C7E",
                                        color: "white",
                                    }}
                                    size="small"
                                    onClick={() => copyToClipboard(conv.phone)}
                                    onMouseEnter={() => setTipText("Copy")}
                                />
                            </Tooltip>
                            {/* <Tooltip title={tipText}>
                                <Chip
                                    icon={
                                        <AiFillMail
                                            style={{
                                                color: "white",
                                                fontSize: "14px",
                                                marginLeft: "8px",
                                            }}
                                        />
                                    }
                                    label={conv.email}
                                    variant="filled"
                                    style={{
                                        backgroundColor: "rgb(205 92 92)",
                                        color: "white",
                                    }}
                                    size="small"
                                    onClick={() => copyToClipboard(conv.email)}
                                    onMouseEnter={() => setTipText("Copy")}
                                />
                            </Tooltip> */}
                        </div>
                    )}
                </div>
            </div>
            {/* {conv?.lastTimestamp ? (
        <div className="font-[Outfit] absolute top-1 right-2 text-xs text-gray-700">
          {moment(parseInt(conv?.lastTimestamp)).fromNow()}
        </div>
      ) : (
        <div className="font-[Outfit] absolute top-1 right-2 text-xs text-gray-700">
          {moment(parseInt(conv?.timestamp)).fromNow()}
        </div>
      )} */}
            <div
                className="absolute top-1 right-2 text-xs text-gray-700"
                style={{ fontFamily: "Nunito Sans, sans-serif" }}
            >
                {moment(finalTime).fromNow()}
                {/* {conv?.lastResponseStudent} */}
            </div>
        </div>
    );
};
export default Index;
